import type { MediaFile } from '../types/api.types';

/**
 * Converts a File object to base64-encoded MediaFile format
 * @param file - The File object to convert
 * @returns Promise<MediaFile> - The converted file with base64 data
 */
export const convertFileToMediaFile = async (
  file: File
): Promise<MediaFile> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const result = reader.result as string;
      // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
      const base64Data = result.split(',')[1];

      resolve({
        file_name: file.name,
        file_data: base64Data,
        mime_type: file.type,
      });
    };

    reader.onerror = () => {
      reject(new Error(`Failed to read file: ${file.name}`));
    };

    reader.readAsDataURL(file);
  });
};

/**
 * Converts an array of File objects to MediaFile array
 * @param files - Array of File objects to convert
 * @returns Promise<MediaFile[]> - Array of converted files with base64 data
 */
export const convertFilesToMediaFiles = async (
  files: File[]
): Promise<MediaFile[]> => {
  try {
    const mediaFiles = await Promise.all(
      files.map(file => convertFileToMediaFile(file))
    );
    return mediaFiles;
  } catch (error) {
    console.error('Error converting files to media files:', error);
    throw error;
  }
};

/**
 * Validates file size and type before conversion
 * @param file - File to validate
 * @param maxSizeBytes - Maximum file size in bytes (default: 10MB)
 * @param allowedTypes - Array of allowed MIME types (default: images)
 * @returns boolean - Whether the file is valid
 */
export const validateFile = (
  file: File,
  maxSizeBytes: number = 10 * 1024 * 1024, // 10MB
  allowedTypes: string[] = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'application/pdf',
    'text/plain',
  ]
): { isValid: boolean; error?: string } => {
  if (file.size > maxSizeBytes) {
    return {
      isValid: false,
      error: `File size exceeds ${Math.round(maxSizeBytes / (1024 * 1024))}MB limit`,
    };
  }

  if (!allowedTypes.includes(file.type)) {
    return {
      isValid: false,
      error: `File type ${file.type} is not supported`,
    };
  }

  return { isValid: true };
};

/**
 * Formats a file size in bytes to a human-readable string
 * @param bytes - The file size in bytes
 * @returns string - Formatted file size (e.g., "1.5 MB", "500 KB")
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};
